// Step 4b: Assistance Selection - Select type of legal assistance to provide
import { useState } from 'react'
import { useVisitWizard } from '../../../../contexts/VisitWizardContext'
import { Button } from '../../../../components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../../../../components/ui/card'
import { RadioGroup, RadioGroupItem } from '../../../../components/ui/radio-group'
import { Label } from '../../../../components/ui/label'
import { Badge } from '../../../../components/ui/badge'
import { CheckCircle2 } from 'lucide-react'
import { AssistanceType, ASSISTANCE_TYPE_LABELS } from '../../../../types/visit-wizard'

// All available assistance types
const ASSISTANCE_TYPES: { id: AssistanceType; label: string }[] = [
  { id: 'legal-counselling', label: ASSISTANCE_TYPE_LABELS['legal-counselling'] },
  { id: 'documentation', label: ASSISTANCE_TYPE_LABELS['documentation'] },
  { id: 'oaths-administered', label: ASSISTANCE_TYPE_LABELS['oaths-administered'] },
  { id: 'jail-visit', label: ASSISTANCE_TYPE_LABELS['jail-visit'] },
  { id: 'representation-court', label: ASSISTANCE_TYPE_LABELS['representation-court'] },
  { id: 'representation-appellate', label: ASSISTANCE_TYPE_LABELS['representation-appellate'] },
  { id: 'prosecutor-office', label: ASSISTANCE_TYPE_LABELS['prosecutor-office'] },
  { id: 'labor-cases', label: ASSISTANCE_TYPE_LABELS['labor-cases'] },
  { id: 'administrative-case', label: ASSISTANCE_TYPE_LABELS['administrative-case'] },
  { id: 'outreach', label: ASSISTANCE_TYPE_LABELS['outreach'] },
  { id: 'mediation-conciliation', label: ASSISTANCE_TYPE_LABELS['mediation-conciliation'] },
]

export function AssistanceSelection() {
  const { state, selectAssistanceType, goToStep } = useVisitWizard()
  const [selectedType, setSelectedType] = useState<AssistanceType | ''>('')

  const handleNext = () => {
    if (selectedType) {
      selectAssistanceType(selectedType as AssistanceType)
    }
  }

  const handleFinalize = () => {
    goToStep('summary')
  }

  const hasAssistances = state.assistances.length > 0

  // Check if assistance type has already been added
  const isTypeAdded = (type: AssistanceType): boolean => {
    return state.assistances.some((a) => a.type === type)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-3xl font-bold text-primary mb-2">Select Legal Assistance</h2>
        <p className="text-sm text-muted-foreground">
          Choose the type of assistance to provide to the client
        </p>
        {hasAssistances && (
          <div className="mt-3 flex items-center gap-2">
            <Badge variant="secondary" className="bg-green-100 text-green-800">
              <CheckCircle2 className="h-3 w-3 mr-1" />
              {state.assistances.length} assistance(s) added
            </Badge>
          </div>
        )}
      </div>

      {/* Assistance Type Selection */}
      <Card>
        <CardHeader>
          <CardTitle>Available Assistance Types</CardTitle>
        </CardHeader>
        <CardContent>
          <RadioGroup
            value={selectedType}
            onValueChange={(value) => setSelectedType(value as AssistanceType)}
          >
            <div className="grid md:grid-cols-2 gap-4">
              {ASSISTANCE_TYPES.map((type) => {
                const isAdded = isTypeAdded(type.id)

                return (
                  <div
                    key={type.id}
                    className={`flex items-start space-x-3 p-4 rounded-lg border-2 transition-all ${
                      isAdded
                        ? 'bg-green-50 border-green-200 opacity-60'
                        : selectedType === type.id
                        ? 'border-primary bg-primary/5'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <RadioGroupItem
                      value={type.id}
                      id={type.id}
                      disabled={isAdded}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <Label
                        htmlFor={type.id}
                        className={`cursor-pointer text-sm leading-relaxed ${
                          isAdded ? 'cursor-not-allowed' : ''
                        }`}
                      >
                        {type.label}
                        {isAdded && (
                          <Badge variant="secondary" className="ml-2 bg-green-100 text-green-800 text-xs">
                            Added
                          </Badge>
                        )}
                      </Label>
                    </div>
                  </div>
                )
              })}
            </div>
          </RadioGroup>
        </CardContent>
      </Card>

      {/* Navigation */}
      <div className="flex justify-between pt-6">
        <Button variant="outline" onClick={() => goToStep('classification')}>
          Back
        </Button>

        <div className="flex gap-3">
          {hasAssistances && (
            <Button
              onClick={handleFinalize}
              variant="outline"
              className="border-primary text-primary hover:bg-primary hover:text-white"
            >
              Review & Finalize ({state.assistances.length})
            </Button>
          )}

          <Button
            onClick={handleNext}
            disabled={!selectedType}
            className="bg-primary hover:bg-primary/90"
          >
            Next
          </Button>
        </div>
      </div>

      {/* Help text */}
      {!hasAssistances && (
        <div className="text-center text-sm text-muted-foreground">
          You must add at least one assistance before finalizing
        </div>
      )}
    </div>
  )
}
